import React, { useEffect, useState } from 'react';

interface ConfettiProps {
  trigger: boolean;
  onComplete?: () => void;
}

interface ConfettiPiece {
  id: number;
  left: number;
  delay: number;
  duration: number;
  color: string;
  rotation: number;
  size: number;
  horizontalMovement: number;
}

const Confetti: React.FC<ConfettiProps> = ({ trigger, onComplete }) => {
  const [pieces, setPieces] = useState<ConfettiPiece[]>([]);
  const [isActive, setIsActive] = useState(false);

  useEffect(() => {
    if (trigger && !isActive) {
      // Get confetti colors from CSS variables
      const rootStyles = getComputedStyle(document.documentElement);
      const COLORS = [
        rootStyles.getPropertyValue('--color-confetti-1').trim(),
        rootStyles.getPropertyValue('--color-confetti-2').trim(),
        rootStyles.getPropertyValue('--color-confetti-3').trim(),
      ];

      // Generate confetti pieces
      const confettiPieces: ConfettiPiece[] = [];
      for (let i = 0; i < 200; i++) {
        confettiPieces.push({
          id: i,
          left: Math.random() * 100, // Random X position across screen (%)
          delay: Math.random() * 3, // Stagger start times (seconds)
          duration: 1.5 + Math.random() * 3, // Fall duration
          color: COLORS[Math.floor(Math.random() * COLORS.length)],
          rotation: Math.random() * 360, // Initial rotation
          size: 8 + Math.random() * 8, // Size 8-16px
          horizontalMovement: (Math.random() - 0.5) * 200, // Random horizontal drift -100 to +100 pixels
        });
      }
      setPieces(confettiPieces);
      setIsActive(true);

      // Clean up after animation completes
      const maxDuration = Math.max(...confettiPieces.map(p => p.duration + p.delay));
      const timeout = setTimeout(() => {
        setIsActive(false);
        setPieces([]);
        if (onComplete) {
          onComplete();
        }
      }, maxDuration * 1000 + 500);

      return () => clearTimeout(timeout);
    }
  }, [trigger]);

  if (!isActive || pieces.length === 0) {
    return null;
  }

  return (
    <div className="confetti-container">
      {pieces.map((piece) => (
        <div
          key={piece.id}
          className="confetti-piece"
          style={{
            left: `${piece.left}%`,
            animationDelay: `${piece.delay}s`,
            animationDuration: `${piece.duration}s`,
            backgroundColor: piece.color,
            width: `${piece.size}px`,
            height: `${piece.size}px`,
            '--rotation': `${piece.rotation}deg`,
            '--horizontal-movement': `${piece.horizontalMovement}px`,
          } as React.CSSProperties}
        />
      ))}
    </div>
  );
};

export default Confetti;
